import { AnimatePresence, motion } from 'motion/react';
import { type FormEvent, type ReactNode, useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAudio } from '../context/AudioContext';
import { useBooking } from '../context/BookingContext';
import { artists, styleLabels, tattooStyles } from '../data';
import { cn } from '../lib/cn';
import { generateBookingCode, saveBooking } from '../lib/storage';
import { lockScroll, unlockScroll } from '../providers/LenisProvider';

type Errors = Partial<Record<'name' | 'email' | 'idea' | 'consent', string>>;

const timeframes = ['now', 'soon', 'flexible'] as const;
const budgets = ['budgetSmall', 'budgetMid', 'budgetLarge', 'budgetXl'] as const;

type State = {
  name: string;
  email: string;
  phone: string;
  artistSlug: string;
  style: string;
  idea: string;
  timeframe: (typeof timeframes)[number];
  budget: (typeof budgets)[number];
  consent: boolean;
};

const initialState = (presetArtist?: string): State => ({
  name: '',
  email: '',
  phone: '',
  artistSlug: presetArtist ?? 'any',
  style: 'any',
  idea: '',
  timeframe: 'soon',
  budget: 'budgetMid',
  consent: false,
});

export function BookingModal() {
  const { t, i18n } = useTranslation();
  const { open, preset, closeBooking } = useBooking();
  const audio = useAudio();
  const titleId = useId();
  const [state, setState] = useState<State>(() => initialState(preset.artistSlug));
  const [errors, setErrors] = useState<Errors>({});
  const [submitting, setSubmitting] = useState(false);
  const [successCode, setSuccessCode] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setState(initialState(preset.artistSlug));
      setErrors({});
      setSuccessCode(null);
      audio.play('open');
    }
  }, [open, preset.artistSlug, audio]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    lockScroll();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeBooking();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      unlockScroll();
      window.removeEventListener('keydown', onKey);
    };
  }, [open, closeBooking]);

  const validate = (s: State): Errors => {
    const e: Errors = {};
    if (!s.name.trim()) e.name = t('booking.errors.required');
    if (!s.email.trim()) e.email = t('booking.errors.required');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.email)) e.email = t('booking.errors.email');
    if (!s.idea.trim() || s.idea.trim().length < 12) e.idea = t('booking.errors.required');
    if (!s.consent) e.consent = t('booking.errors.consent');
    return e;
  };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const found = validate(state);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    setSubmitting(true);
    await new Promise((resolve) => setTimeout(resolve, 850));
    const code = generateBookingCode();
    saveBooking({
      code,
      createdAt: new Date().toISOString(),
      name: state.name.trim(),
      email: state.email.trim(),
      phone: state.phone.trim() || undefined,
      artistSlug: state.artistSlug,
      style: state.style,
      idea: state.idea.trim(),
      timeframe: state.timeframe,
      budget: state.budget,
      language: i18n.resolvedLanguage ?? i18n.language,
    });
    setSubmitting(false);
    setSuccessCode(code);
  };

  const artistOptions = useMemo(() => artists, []);
  const lang = (i18n.resolvedLanguage ?? i18n.language).slice(0, 2) === 'en' ? 'en' : 'it';

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="booking-portal"
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          className="fixed inset-0 z-[200] flex items-stretch md:items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
        >
          <button
            type="button"
            aria-label={t('nav.close')}
            onClick={closeBooking}
            className="absolute inset-0 bg-ink-base/90 backdrop-blur-md"
          />

          <motion.div
            data-lenis-prevent
            className="relative z-10 w-full md:max-w-4xl mx-0 md:mx-6 my-0 md:my-12 bg-ink-base border-y md:border md:border-ink-paper/20 overflow-y-auto max-h-[100svh] md:max-h-[90vh] overscroll-contain"
            initial={{ y: 32, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 16, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          >
            {/* HEADER */}
            <header className="sticky top-0 z-10 bg-ink-base/95 backdrop-blur-sm flex items-start justify-between px-7 py-7 md:px-12 md:py-10 border-b border-ink-paper/15">
              <div>
                <p className="text-base font-display lowercase text-ink-paper/55 tracking-tight">
                  inkhaus, {successCode ? 'ok' : 'prenota'}
                </p>
                <h2
                  id={titleId}
                  className="mt-4 font-display tracking-display leading-[0.95] text-4xl md:text-6xl lowercase"
                >
                  {successCode ? t('booking.success.title') : t('booking.title')}
                </h2>
              </div>
              <button
                type="button"
                onClick={closeBooking}
                className="ml-4 mt-1 shrink-0 size-11 rounded-full border border-ink-paper/40 hover:border-ink-paper hover:bg-ink-paper hover:text-ink-base transition-colors flex items-center justify-center"
                aria-label={t('nav.close')}
                data-magnetic="true"
              >
                <span aria-hidden className="text-2xl leading-none">×</span>
              </button>
            </header>

            {successCode ? (
              <SuccessPanel code={successCode} onClose={closeBooking} />
            ) : (
              <form noValidate onSubmit={onSubmit} className="px-7 py-10 md:px-12 md:py-12">
                <p className="text-lg md:text-xl text-ink-paper/85 max-w-xl mb-12">
                  {t('booking.intro')}
                </p>

                <div className="grid gap-10">
                  <Group title={lang === 'it' ? 'Chi sei' : 'About you'} step="01">
                    <div className="grid gap-6 md:grid-cols-2">
                      <Field
                        id="bk-name"
                        label={t('booking.fields.name')}
                        error={errors.name}
                      >
                        <input
                          id="bk-name"
                          autoComplete="name"
                          placeholder={t('booking.fields.namePlaceholder')}
                          value={state.name}
                          onChange={(e) => setState((s) => ({ ...s, name: e.target.value }))}
                          className={inputClass}
                        />
                      </Field>

                      <Field
                        id="bk-email"
                        label={t('booking.fields.email')}
                        error={errors.email}
                      >
                        <input
                          id="bk-email"
                          type="email"
                          autoComplete="email"
                          placeholder={t('booking.fields.emailPlaceholder')}
                          value={state.email}
                          onChange={(e) => setState((s) => ({ ...s, email: e.target.value }))}
                          className={inputClass}
                        />
                      </Field>

                      <div className="md:col-span-2">
                        <Field
                          id="bk-phone"
                          label={t('booking.fields.phone')}
                          optional
                        >
                          <input
                            id="bk-phone"
                            type="tel"
                            autoComplete="tel"
                            placeholder={t('booking.fields.phonePlaceholder')}
                            value={state.phone}
                            onChange={(e) => setState((s) => ({ ...s, phone: e.target.value }))}
                            className={inputClass}
                          />
                        </Field>
                      </div>
                    </div>
                  </Group>

                  <Group title={lang === 'it' ? "L'idea" : 'The idea'} step="02">
                    <div className="grid gap-6">
                      <div className="grid gap-6 md:grid-cols-2">
                        <Field id="bk-artist" label={t('booking.fields.artist')}>
                          <select
                            id="bk-artist"
                            value={state.artistSlug}
                            onChange={(e) =>
                              setState((s) => ({ ...s, artistSlug: e.target.value }))
                            }
                            className={cn(inputClass, 'appearance-none pr-10 bg-no-repeat bg-right')}
                            style={{
                              backgroundImage:
                                "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'><path fill='%23ededeb' d='M0 0l6 8 6-8z'/></svg>\")",
                              backgroundPosition: 'right 0.5rem center',
                            }}
                          >
                            <option value="any">{t('booking.fields.anyArtist')}</option>
                            {artistOptions.map((a) => (
                              <option key={a.slug} value={a.slug}>
                                {a.name}
                              </option>
                            ))}
                          </select>
                        </Field>

                        <Field id="bk-style" label={t('booking.fields.style')}>
                          <select
                            id="bk-style"
                            value={state.style}
                            onChange={(e) => setState((s) => ({ ...s, style: e.target.value }))}
                            className={cn(inputClass, 'appearance-none pr-10 bg-no-repeat bg-right')}
                            style={{
                              backgroundImage:
                                "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'><path fill='%23ededeb' d='M0 0l6 8 6-8z'/></svg>\")",
                              backgroundPosition: 'right 0.5rem center',
                            }}
                          >
                            <option value="any">{t('common.any')}</option>
                            {tattooStyles.map((s) => (
                              <option key={s} value={s}>
                                {styleLabels[s][lang]}
                              </option>
                            ))}
                          </select>
                        </Field>
                      </div>

                      <Field id="bk-idea" label={t('booking.fields.idea')} error={errors.idea}>
                        <textarea
                          id="bk-idea"
                          rows={5}
                          placeholder={t('booking.fields.ideaPlaceholder')}
                          value={state.idea}
                          onChange={(e) => setState((s) => ({ ...s, idea: e.target.value }))}
                          className={cn(inputClass, 'resize-y min-h-40 leading-relaxed py-3')}
                        />
                      </Field>
                    </div>
                  </Group>

                  <Group title={lang === 'it' ? 'Quando e quanto' : 'When & budget'} step="03">
                    <div className="grid gap-8">
                      <fieldset className="grid gap-3">
                        <legend className="font-mono text-xs uppercase tracking-[0.3em] text-ink-paper/75 mb-2">
                          {t('booking.fields.timeframe')}
                        </legend>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          {timeframes.map((tf) => (
                            <RadioPill
                              key={tf}
                              name="timeframe"
                              value={tf}
                              active={state.timeframe === tf}
                              onSelect={() => setState((s) => ({ ...s, timeframe: tf }))}
                            >
                              {t(
                                `booking.fields.${
                                  tf === 'now'
                                    ? 'timeframeNow'
                                    : tf === 'soon'
                                      ? 'timeframeSoon'
                                      : 'timeframeFlexible'
                                }`,
                              )}
                            </RadioPill>
                          ))}
                        </div>
                      </fieldset>

                      <fieldset className="grid gap-3">
                        <legend className="font-mono text-xs uppercase tracking-[0.3em] text-ink-paper/75 mb-2">
                          {t('booking.fields.budget')}
                        </legend>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {budgets.map((b) => (
                            <RadioPill
                              key={b}
                              name="budget"
                              value={b}
                              active={state.budget === b}
                              onSelect={() => setState((s) => ({ ...s, budget: b }))}
                            >
                              {t(`booking.fields.${b}`)}
                            </RadioPill>
                          ))}
                        </div>
                      </fieldset>
                    </div>
                  </Group>
                </div>

                <div className="mt-12 border-t border-ink-paper/15 pt-8 grid gap-6">
                  <label className="flex items-start gap-4 text-base text-ink-paper/90">
                    <input
                      type="checkbox"
                      checked={state.consent}
                      onChange={(e) => setState((s) => ({ ...s, consent: e.target.checked }))}
                      className="mt-1.5 size-5 accent-[color:var(--color-ink-accent)] cursor-pointer"
                    />
                    <span className="leading-relaxed">{t('booking.fields.consent')}</span>
                  </label>
                  {errors.consent && (
                    <p className="font-mono text-xs uppercase tracking-[0.25em] text-ink-accent">
                      ▸ {errors.consent}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="group inline-flex items-center justify-between gap-3 w-full md:w-auto md:min-w-[340px] px-8 py-6 bg-ink-paper text-ink-base text-sm uppercase tracking-[0.25em] disabled:opacity-50 transition-colors hover:bg-ink-accent hover:text-ink-paper font-medium"
                  >
                    <span>
                      {submitting ? t('booking.fields.sending') : t('booking.fields.submit')}
                    </span>
                    <span aria-hidden className="transition-transform group-hover:translate-x-1">
                      →
                    </span>
                  </button>
                </div>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const inputClass =
  'w-full bg-ink-line/40 border border-ink-paper/15 hover:border-ink-paper/30 focus:border-ink-paper px-4 py-3.5 text-base md:text-lg text-ink-paper placeholder:text-ink-muted/70 focus:outline-none focus:bg-ink-line/60 transition-colors';

function Group({
  title,
  step,
  children,
}: {
  title: string;
  step: string;
  children: ReactNode;
}) {
  return (
    <section className="grid gap-5">
      <header className="flex items-baseline gap-4 border-b border-ink-paper/15 pb-3">
        <span className="text-sm font-display lowercase tracking-tight text-ink-paper/40">
          {step}
        </span>
        <h3 className="font-display tracking-display text-xl md:text-2xl lowercase leading-none">
          {title}
        </h3>
      </header>
      {children}
    </section>
  );
}

function Field({
  id,
  label,
  error,
  optional,
  children,
}: {
  id: string;
  label: string;
  error?: string;
  optional?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="grid gap-2.5">
      <label
        htmlFor={id}
        className="font-mono text-xs uppercase tracking-[0.28em] text-ink-paper/80 flex items-center gap-2"
      >
        {label}
        {optional && (
          <span className="font-sans text-[10px] tracking-normal text-ink-muted normal-case">
            opzionale
          </span>
        )}
      </label>
      {children}
      {error && (
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-ink-accent">
          ▸ {error}
        </p>
      )}
    </div>
  );
}

function RadioPill({
  name,
  value,
  active,
  onSelect,
  children,
}: {
  name: string;
  value: string;
  active: boolean;
  onSelect: () => void;
  children: ReactNode;
}) {
  return (
    <label
      className={cn(
        'flex items-center justify-center text-center text-sm md:text-base py-3.5 px-3 border transition-colors',
        active
          ? 'border-ink-paper bg-ink-paper text-ink-base'
          : 'border-ink-paper/20 text-ink-paper/85 hover:border-ink-paper/60 hover:bg-ink-line/40',
      )}
    >
      <input
        type="radio"
        name={name}
        value={value}
        checked={active}
        onChange={onSelect}
        className="sr-only"
      />
      <span>{children}</span>
    </label>
  );
}

function SuccessPanel({ code, onClose }: { code: string; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="px-7 py-12 md:px-12 md:py-16 grid gap-10">
      <p className="text-xl md:text-2xl text-ink-paper/90 max-w-xl leading-relaxed">
        {t('booking.success.body', { code })}
      </p>
      <div className="grid gap-4 max-w-md">
        <div className="border border-ink-paper/15 bg-ink-line/40 px-6 py-5">
          <p className="text-sm font-display lowercase tracking-tight text-ink-muted">
            Reference
          </p>
          <p className="mt-2 font-mono text-2xl tracking-[0.15em] text-ink-paper">{code}</p>
        </div>
      </div>
      <div className="flex flex-col md:flex-row gap-3">
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center justify-between gap-3 px-7 py-5 bg-ink-paper text-ink-base text-sm uppercase tracking-[0.25em] hover:bg-ink-accent hover:text-ink-paper transition-colors min-w-[260px]"
        >
          <span>{t('booking.success.close')}</span>
          <span aria-hidden>→</span>
        </button>
      </div>
    </div>
  );
}
